import type { AppEdge, AppNode } from './types';
import { getNodeDisplayLabel, type FlowDisplayLanguage } from './displayLanguage';

const reachableNodeIds = (startId: string, edges: AppEdge[]): Set<string> => {
  const targetsBySource = new Map<string, string[]>();
  edges.forEach((edge) => {
    const targets = targetsBySource.get(edge.source) ?? [];
    targets.push(edge.target);
    targetsBySource.set(edge.source, targets);
  });

  const reachable = new Set([startId]);
  const pending = [startId];
  while (pending.length > 0) {
    const source = pending.shift()!;
    for (const target of targetsBySource.get(source) ?? []) {
      if (reachable.has(target)) continue;
      reachable.add(target);
      pending.push(target);
    }
  }
  return reachable;
};

const validateScope = (
  nodes: AppNode[],
  edges: AppEdge[],
  functionNames: string[],
  language: FlowDisplayLanguage,
): string | null => {
  const isFunctionScope = functionNames.length > 0;
  const startType = isFunctionScope ? 'input' : 'start';
  const terminalType = isFunctionScope ? 'output' : 'end';
  const start = nodes.find((node) => node.type === startType);
  const scopeName = isFunctionScope
    ? `Function「${functionNames.join(' > ')}」内の${getNodeDisplayLabel(startType, language)}から${getNodeDisplayLabel(terminalType, language)}`
    : `${getNodeDisplayLabel(startType, language)}から${getNodeDisplayLabel(terminalType, language)}`;
  const warning = `警告: ${scopeName}まで線でつないでください。実行を開始しませんでした。`;

  if (!start) {
    return warning;
  }

  const reachable = reachableNodeIds(start.id, edges);
  if (!nodes.some((node) => node.type === terminalType && reachable.has(node.id))) {
    return warning;
  }

  for (const node of nodes) {
    if (node.type !== 'function' || !reachable.has(node.id)) continue;
    const functionName = node.data.functionName.trim() || '名前なし';
    const warning = validateScope(
      node.data.internalNodes,
      node.data.internalEdges,
      [...functionNames, functionName],
      language,
    );
    if (warning) return warning;
  }

  return null;
};

export const validateFlowConnectivity = (
  nodes: AppNode[],
  edges: AppEdge[],
  language: FlowDisplayLanguage,
): string | null => validateScope(nodes, edges, [], language);
